/**
 * Orders API Module
 * Handles all order-related API calls
 */

import apiClient, { extractData } from '../api-client';

export const orderApi = {
  confirmReceipt(orderId) { return apiClient.post(`/orders/${orderId}/confirm-receipt`).then(extractData); },
  /**
   * List all orders with filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20, max: 100)
   * @param {string} params.status - Filter by status
   * @param {number} params.customer_id - Filter by customer
   * @param {string} params.from_date - Filter from date (YYYY-MM-DD)
   * @param {string} params.to_date - Filter to date (YYYY-MM-DD)
   * @param {string} params.search - Search term
   * @returns {Promise<Object>} Paginated response with orders
   */
  list(params) {
    return apiClient.get('/orders/', {
      params,
      paramsSerializer: (p) => {
        const usp = new URLSearchParams();
        Object.entries(p || {}).forEach(([k, v]) => {
          if (v === null || v === undefined || v === '') return;
          if (Array.isArray(v)) {
            v.forEach((item) => {
              if (item !== null && item !== undefined && item !== '') usp.append(k, item);
            });
          } else {
            usp.append(k, v);
          }
        });
        return usp.toString();
      },
    }).then(extractData);
  },

  /**
   * Get demo image URLs for an order by its public code (no auth).
   * @param {string} orderCode
   */
  getDemoImagesByCode(orderCode) {
    return apiClient
      .get(`/orders/demo/${encodeURIComponent(orderCode)}`)
      .then(extractData);
  },

  /**
   * Get order by ID with all details
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Order response with items and history
   */
  getById(orderId) {
    return apiClient.get(`/orders/${orderId}`).then(extractData);
  },

  /**
   * Create a new order
   * @param {Object} orderData - Order data
   * @param {number} orderData.customer_id - Customer ID (required)
   * @param {Array} orderData.items - Order items (required)
   * @param {number} orderData.delivery_days - Delivery days
   * @param {string} orderData.discount_type - Discount type (PERCENT or AMOUNT)
   * @param {number|string} orderData.discount_value - Discount value
   * @param {number|string} orderData.tax_percent - Tax percentage
   * @param {number|string} orderData.shipping_fee - Shipping fee
   * @param {string} orderData.notes - Notes
   * @returns {Promise<Object>} Created order response
   */
  create(orderData) {
    return apiClient.post('/orders/', orderData).then(extractData);
  },

  /**
   * Update order (only DRAFT status)
   * @param {number} orderId - Order ID
   * @param {Object} orderData - Order data to update
   * @returns {Promise<Object>} Updated order response
   */
  update(orderId, orderData) {
    return apiClient.put(`/orders/${orderId}`, orderData).then(extractData);
  },


  /**
   * Delete order (DRAFT only)
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Message response
   */
  delete(orderId) {
    return apiClient.delete(`/orders/${orderId}`).then(extractData);
  },

  /**
   * Gửi đơn sang CMS tổng để xưởng sản xuất.
   *
   * Gửi ĐỒNG BỘ ngay trong request này — trả về 200 nghĩa là tổng đã nhận,
   * trả lỗi nghĩa là chưa đổi gì cả (thử lại được ngay). Sửa đơn SAU KHI đã ở
   * "Chờ Sản Xuất" không cần gọi riêng gì nữa — PUT /orders/{id} tự gửi lại
   * bản sửa nếu nội dung ảnh hưởng tới xưởng thay đổi.
   * @param {number} orderId
   * @param {Object} [data] - Tuỳ chọn `{ send_plan }` cho tin Zalo tự gửi khi sang Chờ Sản Xuất
   * @returns {Promise<Object>} Đơn đã đổi sang trạng thái Chờ Sản Xuất
   */
  /**
   * Kéo giá sỉ mới từ tổng rồi trả các dòng lệch so với lúc thêm vào đơn.
   * Gọi ngay trước khi gửi xưởng (helpers/confirmWholesale.js).
   * @returns {Promise<{synced: boolean, orders: Array}>}
   */
  wholesaleCheck(orderIds) {
    return apiClient.post('/orders/wholesale-check', { order_ids: orderIds }).then(extractData);
  },

  sendToCentral(orderId, data) {
    return apiClient.post(`/orders/${orderId}/send-to-central`, data).then(extractData);
  },

  /**
   * Lên Market (DRAFT -> ON_MARKET). Đơn đã đủ thông tin, sẵn sàng vào luồng bán hàng.
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  goToMarket(orderId, data) {
    return apiClient.post(`/orders/${orderId}/go-to-market`, data).then(extractData);
  },

  /**
   * Chốt Market (ON_MARKET -> MARKET_CLOSED). Đơn đã chốt với khách.
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  closeMarket(orderId, data) {
    return apiClient.post(`/orders/${orderId}/close-market`, data).then(extractData);
  },

  /**
   * Gửi tay bộ tin nhắn Zalo theo mẫu của TRẠNG THÁI HIỆN TẠI của đơn (không đổi
   * trạng thái). Là đường duy nhất nhắn khách cho các trạng thái do CMS tổng đẩy về
   * (Đang SX, Hoàn tất SX, Đã bàn giao CTV) — chúng không tự gửi.
   * @param {number} orderId - Order ID
   * @param {Object} [data] - `{ send_plan: ["tn1", ...] }` thứ tự + tin được chọn
   * @returns {Promise<Object>} Message response
   */
  sendZaloMessage(orderId, data) {
    return apiClient.post(`/orders/${orderId}/send-zalo-message`, data).then(extractData);
  },

  /**
   * Đánh dấu đã lấy hàng thủ công (AWAITING_PICKUP -> PICKED_UP)
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  markPickedUp(orderId, data) {
    return apiClient.post(`/orders/${orderId}/update-status`, { status: 'PICKED_UP', notes: data?.notes }).then(extractData);
  },

  /**
   * Tạo vận đơn ở GHN (không đổi status đơn hàng).
   * Status sẽ được cập nhật qua webhook GHN.
   */
  sendToCarrier(orderId, data) {
    return apiClient.post(`/orders/${orderId}/send-to-carrier`, data).then(extractData);
  },

  syncShipping(orderId) {
    return apiClient.post(`/orders/${orderId}/sync-shipping`).then(extractData);
  },

  /**
   * Đánh dấu trạng thái vận chuyển = đã giao thủ công (chỉ đơn đã Hoàn thành).
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Updated order response
   */
  markShippingDelivered(orderId) {
    return apiClient.post(`/orders/${orderId}/mark-shipping-delivered`).then(extractData);
  },

  /**
   * Cancel order (any status -> CANCELLED)
   * @param {number} orderId - Order ID
   * @param {Object} data - Cancellation data
   * @param {string} data.reason - Cancellation reason (required)
   * @returns {Promise<Object>} Updated order response
   */
  cancel(orderId, data) {
    return apiClient.post(`/orders/${orderId}/cancel`, data).then(extractData);
  },

  /**
   * Cancel current shipment only (keeps order.status unchanged) so it can be re-shipped with another carrier
   * @param {number} orderId - Order ID
   * @param {Object} data - Cancellation data
   * @param {string} data.reason - Cancellation reason (required)
   * @returns {Promise<Object>} Updated order response
   */
  cancelShipment(orderId, data) {
    return apiClient.post(`/orders/${orderId}/cancel-shipment`, data).then(extractData);
  },

  markPaid(orderId) {
    return apiClient.post(`/orders/${orderId}/mark-paid`).then(extractData);
  },

  markUnpaid(orderId) {
    return apiClient.post(`/orders/${orderId}/mark-unpaid`).then(extractData);
  },

  markDeposited(orderId, depositAmount = 0) {
    return apiClient.post(`/orders/${orderId}/mark-deposited`, { deposit_amount: depositAmount }).then(extractData);
  },

  updateStatus(orderId, status, notes = null, sendPlan = undefined) {
    return apiClient.post(`/orders/${orderId}/update-status`, { status, notes, send_plan: sendPlan }).then(extractData);
  },

  startReturn(orderId, data) {
    return apiClient.post(`/orders/${orderId}/update-status`, { status: 'RETURNING', notes: data?.notes }).then(extractData);
  },

  completeReturn(orderId, data) {
    return apiClient.post(`/orders/${orderId}/update-status`, { status: 'RETURNED', notes: data?.notes }).then(extractData);
  },

  /**
   * Add item to order (DRAFT only)
   * @param {number} orderId - Order ID
   * @param {Object} itemData - Order item data
   * @param {number} itemData.product_id - Product ID (required)
   * @param {number} itemData.quantity - Quantity (required)
   * @param {number|string} itemData.unit_price - Unit price (required)
   * @param {string} itemData.notes - Notes
   * @param {Array} itemData.print_list - Print list items
   * @returns {Promise<Object>} Created order item response
   */
  addItem(orderId, itemData) {
    return apiClient.post(`/orders/${orderId}/items`, itemData).then(extractData);
  },

  /**
   * Update order item (DRAFT only)
   * @param {number} orderId - Order ID
   * @param {number} itemId - Order item ID
   * @param {Object} itemData - Item data to update
   * @returns {Promise<Object>} Updated order item response
   */
  updateItem(orderId, itemId, itemData) {
    return apiClient.put(`/orders/${orderId}/items/${itemId}`, itemData).then(extractData);
  },

  /**
   * Delete order item (DRAFT only)
   * @param {number} orderId - Order ID
   * @param {number} itemId - Order item ID
   * @returns {Promise<Object>} Message response
   */
  deleteItem(orderId, itemId) {
    return apiClient.delete(`/orders/${orderId}/items/${itemId}`).then(extractData);
  },

  /**
   * Update print list for order item
   * @param {number} orderId - Order ID
   * @param {number} itemId - Order item ID
   * @param {Array} printList - Print list items (replaces entire list)
   * @returns {Promise<Object>} Message response
   */
  updatePrintList(orderId, itemId, printList) {
    return apiClient.put(`/orders/${orderId}/items/${itemId}/print-list`, printList).then(extractData);
  },

  uploadPrintFiles(orderId, formData, options = {}) {
    return apiClient.post(`/orders/${orderId}/print-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...options,
    }).then(extractData);
  },

  deletePrintFileByName(orderId, filename) {
    return apiClient.delete(`/orders/${orderId}/print-file/${encodeURIComponent(filename)}`).then(extractData);
  },

  uploadAiFiles(orderId, formData, options = {}) {
    return apiClient.post(`/orders/${orderId}/ai-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...options,
    }).then(extractData);
  },

  deleteAiFileByName(orderId, filename) {
    return apiClient.delete(`/orders/${orderId}/ai-file/${encodeURIComponent(filename)}`).then(extractData);
  },

  deleteDemoImageByName(orderId, filename) {
    return apiClient.delete(`/orders/${orderId}/demo-image/${encodeURIComponent(filename)}`).then(extractData);
  },

  uploadNoteImages(orderId, formData, options = {}) {
    return apiClient.post(`/orders/${orderId}/note-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...options,
    }).then(extractData);
  },

  deleteNoteImageByName(orderId, filename) {
    return apiClient.delete(`/orders/${orderId}/note-image/${encodeURIComponent(filename)}`).then(extractData);
  },

  uploadInvoiceImage(orderId, formData) {
    return apiClient.post(`/orders/${orderId}/invoice-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(extractData);
  },

  /**
   * Ảnh danh sách in do backend vẽ — CÙNG template + cache với ảnh tn2 gửi Zalo.
   * @param {'zalo'|'a4'} layout zalo: xem trước ảnh sẽ gửi; a4: mỗi ảnh một trang PDF.
   * @returns {Promise<{sections: {item_id:number, product_name:string, images:string[]}[]}>}
   */
  getPrintListImages(orderId, layout = 'zalo') {
    // Lần vẽ đầu (chưa có cache) còn vẽ cả ảnh demo — nới hơn 30s mặc định.
    return apiClient
      .get(`/orders/${orderId}/print-list-images`, { params: { layout }, timeout: 120000 })
      .then(extractData);
  },

  // Ảnh in demo gắn theo TỪNG danh sách in (mỗi sản phẩm trong đơn một danh sách).
  uploadItemDemoImages(orderId, itemId, formData, options = {}) {
    return apiClient.post(`/orders/${orderId}/items/${itemId}/demo-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...options,
    }).then(extractData);
  },

  deleteItemDemoImageByName(orderId, itemId, filename) {
    return apiClient
      .delete(`/orders/${orderId}/items/${itemId}/demo-image/${encodeURIComponent(filename)}`)
      .then(extractData);
  },

  listTags() {
    return apiClient.get('/orders/tags').then(extractData);
  },

  createTag(name, parentId = null) {
    const data = { name };
    if (parentId) data.parent_id = parentId;
    return apiClient.post('/orders/tags', data).then(extractData);
  },

  deleteTag(tagId) {
    return apiClient.delete(`/orders/tags/${tagId}`).then(extractData);
  },
};
